import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useQuery,
} from '@tanstack/react-query';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import ArrowBackRoundedIcon
  from '@mui/icons-material/ArrowBackRounded';

import CloseRoundedIcon
  from '@mui/icons-material/CloseRounded';

import {
  getMarginCenterCustomerContext,
  getMarginCenterCustomerProductContext,
  getMarginCenterExplorer,
  getMarginCenterProductContext,
} from '../services/marginCenterService';

import {
  getPricingDecisionCustomerSchema,
  getPricingDecisionProductSchema,
} from '../services/pricingDecisionService';

import CustomerRelationshipBenchmarkPanel
  from './CustomerRelationshipBenchmarkPanel';

import PricingDecisionDynamicComparablesPanel
  from './PricingDecisionDynamicComparablesPanel';

import PricingDecisionOptimizationPanel
  from './PricingDecisionOptimizationPanel';

import type {
  PricingDecisionAttributeSchema,
  PricingDecisionContextEvidence,
} from '../types/pricingDecision';

import type {
  MarginCenterComparisonMode,
  MarginCenterCustomerContextParams,
  MarginCenterProductContextParams,
  MarginCenterTimeGrain,
} from '../types/marginCenter';


interface Props {
  open: boolean;
  onClose: () => void;

  onOpenObservedRelationship?: (
    customerId: string,
    productId: string,
  ) => void;

  source: 'customer' | 'product';

  customerId: string | null;
  productId: string | null;

  startDate: string | null;
  endDate: string | null;

  comparisonMode:
    MarginCenterComparisonMode;

  comparisonStartDate:
    string | null;

  comparisonEndDate:
    string | null;

  timeGrain:
    MarginCenterTimeGrain;

  currencyCode:
    string | null;
}


interface EntityOption {
  id: string;
  label: string;
}


const numberFormatter =
  new Intl.NumberFormat(
    'es-ES',
    {
      maximumFractionDigits: 2,
    },
  );


function formatNumber(
  value:
    number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return numberFormatter.format(
    value,
  );
}


function formatCurrency(
  value:
    number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  if (!currencyCode) {
    return formatNumber(value);
  }

  try {
    return new Intl.NumberFormat(
      'es-ES',
      {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      },
    ).format(value);
  } catch {
    return formatNumber(value);
  }
}


function formatPct(
  value:
    number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    numberFormatter.format(value)
  } %`;
}


function formatSignedPct(
  value:
    number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${
    numberFormatter.format(value)
  } %`;
}


function formatSignedPoints(
  value:
    number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${
    numberFormatter.format(value)
  } pp`;
}


function formatSignedInteger(
  value:
    number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${
    numberFormatter.format(value)
  }`;
}


function ContextMetric({
  label,
  value,
  change,
}: {
  label: string;
  value: string;
  change?: string;
}) {
  return (
    <Box
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#64748b',
          fontWeight: 750,
        }}
      >
        {label}
      </Typography>

      <Stack
        direction="row"
        spacing={0.75}
        alignItems="baseline"
        useFlexGap
        flexWrap="wrap"
        sx={{
          mt: 0.15,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
          }}
        >
          {value}
        </Typography>

        {change && (
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontWeight: 750,
            }}
          >
            {change}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}


function AttributeLine({
  label,
  value,
}: {
  label: string;
  value:
    string | number | null | undefined;
}) {
  if (
    value === null
    || value === undefined
    || value === ''
  ) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      justifyContent="space-between"
      alignItems="baseline"
    >
      <Typography
        variant="caption"
        sx={{
          color: '#64748b',
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: '#cbd5e1',
          fontWeight: 750,
          textAlign: 'right',
        }}
      >
        {String(value)}
      </Typography>
    </Stack>
  );
}


function CounterpartSearch({
  label,
  options,
  loading,
  inputValue,
  onInputChange,
  onSelect,
}: {
  label: string;
  options: EntityOption[];
  loading: boolean;
  inputValue: string;
  onInputChange: (value: string) => void;
  onSelect: (
    id: string,
    label: string,
  ) => void;
}) {
  return (
    <Autocomplete<
      EntityOption,
      false,
      false,
      true
    >
      freeSolo
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(
        _,
        value,
        reason,
      ) => {
        if (
          reason === 'reset'
        ) {
          return;
        }

        onInputChange(value);
      }}
      onChange={(
        _,
        value,
      ) => {
        if (
          typeof value
          === 'string'
        ) {
          const normalized =
            value.trim();

          if (normalized) {
            onSelect(
              normalized,
              normalized,
            );
          }

          return;
        }

        if (!value) {
          return;
        }

        onSelect(
          value.id,
          value.label,
        );
      }}
      getOptionLabel={(
        option,
      ) =>
        typeof option
          === 'string'
          ? option
          : `${
            option.label
          } · ${
            option.id
          }`
      }
      renderInput={(
        params,
      ) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          helperText={
            'Selecciona una sugerencia o escribe '
            + 'el identificador completo y pulsa Enter.'
          }
          InputProps={{
            ...params.InputProps,

            endAdornment: (
              <>
                {loading && (
                  <CircularProgress
                    size={18}
                  />
                )}

                {
                  params
                    .InputProps
                    .endAdornment
                }
              </>
            ),
          }}
        />
      )}
    />
  );
}


function parsePositiveNumber(
  value: string,
): number | null {
  const normalized =
    value.trim();

  if (!normalized) {
    return null;
  }

  const parsed =
    Number(
      normalized.replace(
        ',',
        '.',
      ),
    );

  if (
    !Number.isFinite(parsed)
    || parsed <= 0
  ) {
    return null;
  }

  return parsed;
}


function DynamicProductFields({
  schema,
  values,
  onChange,
}: {
  schema:
    PricingDecisionAttributeSchema[];

  values:
    Record<string, string>;

  onChange: (
    name: string,
    value: string,
  ) => void;
}) {
  return (
    <Stack
      spacing={1.1}
    >
      {schema.map(
        (field) => {
          if (
            field.name
            === 'standard_cost'
          ) {
            return null;
          }

          if (
            field.type
            === 'select'
          ) {
            return (
              <TextField
                key={
                  field.name
                }
                select
                fullWidth
                size="small"
                label={
                  field.label
                }
                required={
                  field.required
                }
                value={
                  values[
                    field.name
                  ]
                  ?? ''
                }
                onChange={(
                  event,
                ) =>
                  onChange(
                    field.name,
                    event
                      .target
                      .value,
                  )
                }
              >
                {(
                  field.options
                  ?? []
                ).map(
                  (
                    option,
                  ) => (
                    <MenuItem
                      key={
                        option
                      }
                      value={
                        option
                      }
                    >
                      {option}
                    </MenuItem>
                  ),
                )}
              </TextField>
            );
          }

          return (
            <TextField
              key={
                field.name
              }
              fullWidth
              size="small"
              type={
                field.type
                === 'number'
                  ? 'number'
                  : 'text'
              }
              label={
                field.label
              }
              required={
                field.required
              }
              value={
                values[
                  field.name
                ]
                ?? ''
              }
              onChange={(
                event,
              ) =>
                onChange(
                  field.name,
                  event
                    .target
                    .value,
                )
              }
            />
          );
        },
      )}
    </Stack>
  );
}


function DynamicCustomerFields({
  schema,
  values,
  onChange,
}: {
  schema:
    PricingDecisionAttributeSchema[];

  values:
    Record<string, string>;

  onChange: (
    name: string,
    value: string,
  ) => void;
}) {
  return (
    <Stack
      spacing={1.1}
    >
      {schema.map(
        (field) => {
          if (
            field.type
            === 'select'
          ) {
            return (
              <TextField
                key={
                  field.name
                }
                select
                fullWidth
                size="small"
                label={
                  field.label
                }
                required={
                  field.required
                }
                value={
                  values[
                    field.name
                  ]
                  ?? ''
                }
                onChange={(
                  event,
                ) =>
                  onChange(
                    field.name,
                    event
                      .target
                      .value,
                  )
                }
              >
                {(
                  field.options
                  ?? []
                ).map(
                  (
                    option,
                  ) => (
                    <MenuItem
                      key={
                        option
                      }
                      value={
                        option
                      }
                    >
                      {option}
                    </MenuItem>
                  ),
                )}
              </TextField>
            );
          }

          return (
            <TextField
              key={
                field.name
              }
              fullWidth
              size="small"
              type={
                field.type
                === 'number'
                  ? 'number'
                  : 'text'
              }
              label={
                field.label
              }
              required={
                field.required
              }
              value={
                values[
                  field.name
                ]
                ?? ''
              }
              onChange={(
                event,
              ) =>
                onChange(
                  field.name,
                  event
                    .target
                    .value,
                )
              }
            />
          );
        },
      )}
    </Stack>
  );
}


export default function PricingDecisionExistingPairWorkspace({
  open,
  onClose,
  onOpenObservedRelationship,
  source,
  customerId,
  productId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  timeGrain,
  currencyCode,
}: Props) {
  const counterpartDimension =
    source === 'customer'
      ? 'product'
      : 'customer';

  const [
    counterpartInput,
    setCounterpartInput,
  ] =
    useState('');

  const [
    selectedCounterpartId,
    setSelectedCounterpartId,
  ] =
    useState<string | null>(
      null,
    );

  const [
    debouncedSearch,
    setDebouncedSearch,
  ] =
    useState('');

  const [
    counterpartState,
    setCounterpartState,
  ] =
    useState<
      'existing'
      | 'new'
    >(
      'existing',
    );

  const [
    newProductCostInput,
    setNewProductCostInput,
  ] =
    useState('');

  const [
    newProductAttributes,
    setNewProductAttributes,
  ] =
    useState<
      Record<
        string,
        string
      >
    >(
      {},
    );

  const [
    newCustomerAttributes,
    setNewCustomerAttributes,
  ] =
    useState<
      Record<
        string,
        string
      >
    >(
      {},
    );

  useEffect(() => {
    const timer =
      window.setTimeout(
        () => {
          setDebouncedSearch(
            counterpartInput.trim(),
          );
        },
        300,
      );

    return () =>
      window.clearTimeout(
        timer,
      );
  }, [
    counterpartInput,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCounterpartInput('');
    setSelectedCounterpartId(
      null,
    );
    setDebouncedSearch('');

    setCounterpartState(
      'existing',
    );

    setNewProductCostInput(
      '',
    );

    setNewProductAttributes(
      {},
    );

    setNewCustomerAttributes(
      {},
    );
  }, [
    open,
    source,
    customerId,
    productId,
  ]);

  const isNewProduct =
    source === 'customer'
    && counterpartState
      === 'new';

  const isNewCustomer =
    source === 'product'
    && counterpartState
      === 'new';

  const resolvedCustomerId =
    isNewCustomer
      ? null
      : (
        customerId
        ?? (
          source === 'product'
            ? selectedCounterpartId
            : null
        )
      );

  const resolvedProductId =
    isNewProduct
      ? null
      : (
        productId
        ?? (
          source === 'customer'
            ? selectedCounterpartId
            : null
        )
      );

  const pairComplete =
    Boolean(
      resolvedCustomerId
      && resolvedProductId,
    );

  const customerParams =
    useMemo<
      MarginCenterCustomerContextParams
    >(
      () => ({
        start_date:
          startDate
          ?? undefined,

        end_date:
          endDate
          ?? undefined,

        comparison_mode:
          comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate
              ?? undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate
              ?? undefined
            : undefined,

        time_grain:
          timeGrain,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        startDate,
        timeGrain,
      ],
    );

  const productParams =
    useMemo<
      MarginCenterProductContextParams
    >(
      () => ({
        start_date:
          startDate
          ?? undefined,

        end_date:
          endDate
          ?? undefined,

        comparison_mode:
          comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate
              ?? undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate
              ?? undefined
            : undefined,

        time_grain:
          timeGrain,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        startDate,
        timeGrain,
      ],
    );

  const relationshipQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'pricing-decision-pair',
        resolvedCustomerId,
        resolvedProductId,
        customerParams,
      ],

      queryFn: () =>
        getMarginCenterCustomerProductContext(
          resolvedCustomerId
          ?? '',
          resolvedProductId
          ?? '',
          {
            ...customerParams,

            recent_transactions_limit:
              5,
          },
        ),

      enabled:
        open
        && pairComplete,
    });

  const relationship =
    relationshipQuery.data;

  const hasJointHistory =
    relationship?.status === 'ok'
    && relationship
      .relationship
      ?.has_history_as_of_selection_end
      === true;

  const isEvaluablePair =
    relationship?.status === 'ok'
    || relationship?.status
      === 'candidate_relationship';

  useEffect(() => {
    if (
      !open
      || counterpartState !== 'existing'
      || !hasJointHistory
      || !resolvedCustomerId
      || !resolvedProductId
      || !onOpenObservedRelationship
    ) {
      return;
    }

    onOpenObservedRelationship(
      resolvedCustomerId,
      resolvedProductId,
    );
  }, [
    counterpartState,
    hasJointHistory,
    onOpenObservedRelationship,
    open,
    resolvedCustomerId,
    resolvedProductId,
  ]);

  const counterpartSearchQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'pricing-decision-counterpart-search',
        counterpartDimension,
        debouncedSearch,
        startDate,
        endDate,
      ],

      queryFn: () =>
        getMarginCenterExplorer({
          dimension:
            counterpartDimension,

          search:
            debouncedSearch
            || undefined,

          start_date:
            startDate
            ?? undefined,

          end_date:
            endDate
            ?? undefined,

          sort_field:
            'entity_label',

          sort_direction:
            'asc',

          limit: 20,
        }),

      enabled:
        open
        && !pairComplete
        && counterpartState
          === 'existing',

      staleTime: 30_000,
    });

  const counterpartOptions =
    useMemo<EntityOption[]>(
      () => (
        counterpartSearchQuery
          .data
          ?.rows
        ?? []
      ).map(
        (row) => ({
          id:
            row.entity.key,

          label:
            row.entity.label
            || row.entity.key,
        }),
      ),
      [
        counterpartSearchQuery
          .data,
      ],
    );

  const productSchemaQuery =
    useQuery({
      queryKey: [
        'pricing-decision',
        'new-product-schema',
      ],

      queryFn:
        getPricingDecisionProductSchema,

      enabled:
        open
        && isNewProduct,

      staleTime:
        15 * 60 * 1000,
    });

  const productSchema =
    productSchemaQuery
      .data
      ?.data
      ?.new_product_features
    ?? [];

  const newProductStandardCost =
    parsePositiveNumber(
      newProductCostInput,
    );

  const normalizedNewProductAttributes =
    useMemo<
      Record<
        string,
        unknown
      >
    >(
      () => {
        const result:
          Record<
            string,
            unknown
          > = {};

        productSchema.forEach(
          (field) => {
            if (
              field.name
              === 'standard_cost'
            ) {
              return;
            }

            const rawValue =
              newProductAttributes[
                field.name
              ];

            if (
              rawValue === undefined
              || rawValue.trim()
                === ''
            ) {
              return;
            }

            if (
              field.type
              === 'number'
            ) {
              const parsed =
                Number(
                  rawValue.replace(
                    ',',
                    '.',
                  ),
                );

              if (
                Number.isFinite(
                  parsed,
                )
              ) {
                result[
                  field.name
                ] = parsed;
              }

              return;
            }

            result[
              field.name
            ] = rawValue;
          },
        );

        return result;
      },
      [
        newProductAttributes,
        productSchema,
      ],
    );

  const requiredNewProductAttributesComplete =
    productSchema
      .filter(
        (field) =>
          field.required
          && field.name
            !== 'standard_cost',
      )
      .every(
        (field) =>
          Boolean(
            newProductAttributes[
              field.name
            ]?.trim(),
          ),
      );

  const newProductReady =
    isNewProduct
    && Boolean(
      resolvedCustomerId,
    )
    && newProductStandardCost
      !== null
    && requiredNewProductAttributesComplete;

  const customerSchemaQuery =
    useQuery({
      queryKey: [
        'pricing-decision',
        'new-customer-schema',
      ],

      queryFn:
        getPricingDecisionCustomerSchema,

      enabled:
        open
        && isNewCustomer,

      staleTime:
        15 * 60 * 1000,
    });

  const customerSchema =
    customerSchemaQuery
      .data
      ?.data
      ?.new_client_features
    ?? [];

  const normalizedNewCustomerAttributes =
    useMemo<
      Record<
        string,
        unknown
      >
    >(
      () => {
        const result:
          Record<
            string,
            unknown
          > = {};

        customerSchema.forEach(
          (field) => {
            const rawValue =
              newCustomerAttributes[
                field.name
              ];

            if (
              rawValue === undefined
              || rawValue.trim()
                === ''
            ) {
              return;
            }

            if (
              field.type
              === 'number'
            ) {
              const parsed =
                Number(
                  rawValue.replace(
                    ',',
                    '.',
                  ),
                );

              if (
                Number.isFinite(
                  parsed,
                )
              ) {
                result[
                  field.name
                ] = parsed;
              }

              return;
            }

            result[
              field.name
            ] = rawValue;
          },
        );

        return result;
      },
      [
        customerSchema,
        newCustomerAttributes,
      ],
    );

  const requiredNewCustomerAttributesComplete =
    customerSchema
      .filter(
        (field) =>
          field.required,
      )
      .every(
        (field) =>
          Boolean(
            newCustomerAttributes[
              field.name
            ]?.trim(),
          ),
      );

  const newCustomerReady =
    isNewCustomer
    && Boolean(
      resolvedProductId,
    )
    && !customerSchemaQuery
      .isLoading
    && !customerSchemaQuery
      .isError
    && requiredNewCustomerAttributesComplete;

  const customerQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'pricing-decision-customer-context',
        resolvedCustomerId,
        customerParams,
      ],

      queryFn: () =>
        getMarginCenterCustomerContext(
          resolvedCustomerId
          ?? '',
          customerParams,
        ),

      enabled:
        open
        && Boolean(
          resolvedCustomerId,
        ),
    });

  const productQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'pricing-decision-product-context',
        resolvedProductId,
        productParams,
      ],

      queryFn: () =>
        getMarginCenterProductContext(
          resolvedProductId
          ?? '',
          productParams,
        ),

      enabled:
        open
        && Boolean(
          resolvedProductId,
        ),
    });

  const customerContext =
    customerQuery.data;

  const productContext =
    productQuery.data;

  const customer =
    customerContext
      ?.customer
    ?? null;

  const product =
    productContext
      ?.product
    ?? null;

  const customerCurrent =
    customerContext
      ?.current
    ?? null;

  const productCurrent =
    productContext
      ?.current
    ?? null;

  const customerChanges =
    customerContext
      ?.changes
    ?? null;

  const productChanges =
    productContext
      ?.changes
    ?? null;

  const contextEvidence =
    useMemo<
      PricingDecisionContextEvidence
    >(
      () => {
        const sections:
          PricingDecisionContextEvidence[
            'sections'
          ] = [];

        if (isNewCustomer) {
          sections.push({
            kind: 'customer',
            eyebrow: 'Cliente',
            title: 'Cliente nuevo',
            statusLabel:
              'Sin histórico propio',
            description:
              'La evaluación utiliza los atributos aportados '
              + 'para representar al nuevo cliente.',
            metrics: [],
            attributes:
              Object.entries(
                normalizedNewCustomerAttributes,
              ).map(
                ([name, value]) => ({
                  label:
                    customerSchema.find(
                      (field) =>
                        field.name === name,
                    )?.label
                    ?? name,

                  value:
                    String(value),
                }),
              ),
          });
        } else if (
          customer
          && customerCurrent
        ) {
          sections.push({
            kind: 'customer',
            eyebrow: 'Referencia cliente',
            title: customer.label,
            statusLabel:
              'Histórico disponible',
            description:
              'Comportamiento agregado del cliente en '
              + 'el periodo seleccionado.',
            metrics: [
              {
                label:
                  'Precio neto medio',
                value:
                  formatCurrency(
                    customerCurrent
                      .average_net_unit_price,
                    currencyCode,
                  ),
              },
              {
                label:
                  'Margen s/ingresos',
                value:
                  formatPct(
                    customerCurrent
                      .margin_percentage,
                  ),
              },
              {
                label: 'Unidades',
                value:
                  formatNumber(
                    customerCurrent.units,
                  ),
              },
              {
                label:
                  'Productos activos',
                value:
                  formatNumber(
                    customerCurrent
                      .active_products,
                  ),
              },
            ],
            attributes: [
              ...(
                customer
                  .attributes
                  .customer_segment
                  ? [{
                    label: 'Segmento',
                    value:
                      customer
                        .attributes
                        .customer_segment,
                  }]
                  : []
              ),
              ...(
                customer
                  .attributes
                  .region
                  ? [{
                    label: 'Región',
                    value:
                      customer
                        .attributes
                        .region,
                  }]
                  : []
              ),
              ...(
                customer
                  .attributes
                  .country
                  ? [{
                    label: 'País',
                    value:
                      customer
                        .attributes
                        .country,
                  }]
                  : []
              ),
            ],
          });
        }

        if (isNewProduct) {
          sections.push({
            kind: 'product',
            eyebrow: 'Producto',
            title: 'Producto nuevo',
            statusLabel:
              'Sin histórico propio',
            description:
              'La evaluación utiliza el coste y los '
              + 'atributos aportados para este producto.',
            metrics: [
              {
                label:
                  'Coste estándar',
                value:
                  formatCurrency(
                    newProductStandardCost,
                    currencyCode,
                  ),
              },
            ],
            attributes:
              Object.entries(
                normalizedNewProductAttributes,
              ).map(
                ([name, value]) => ({
                  label:
                    productSchema.find(
                      (field) =>
                        field.name === name,
                    )?.label
                    ?? name,

                  value:
                    String(value),
                }),
              ),
          });
        } else if (
          product
          && productCurrent
        ) {
          sections.push({
            kind: 'product',
            eyebrow: 'Referencia producto',
            title: product.label,
            statusLabel:
              'Histórico disponible',
            description:
              'Comportamiento agregado del producto en '
              + 'el periodo seleccionado.',
            metrics: [
              {
                label:
                  'Precio neto medio',
                value:
                  formatCurrency(
                    productCurrent
                      .average_net_unit_price,
                    currencyCode,
                  ),
              },
              {
                label:
                  'Margen s/ingresos',
                value:
                  formatPct(
                    productCurrent
                      .margin_percentage,
                  ),
              },
              {
                label: 'Unidades',
                value:
                  formatNumber(
                    productCurrent.units,
                  ),
              },
              {
                label:
                  'Clientes activos',
                value:
                  formatNumber(
                    productCurrent
                      .active_customers,
                  ),
              },
            ],
            attributes: [
              ...(
                product
                  .attributes
                  .product_category
                  ? [{
                    label: 'Categoría',
                    value:
                      product
                        .attributes
                        .product_category,
                  }]
                  : []
              ),
              ...(
                product
                  .attributes
                  .product_brand
                  ? [{
                    label: 'Marca',
                    value:
                      product
                        .attributes
                        .product_brand,
                  }]
                  : []
              ),
              ...(
                product
                  .attributes
                  .standard_cost
                  !== null
                && product
                  .attributes
                  .standard_cost
                  !== undefined
                  ? [{
                    label:
                      'Coste estándar',
                    value:
                      formatCurrency(
                        product
                          .attributes
                          .standard_cost,
                        currencyCode,
                      ),
                  }]
                  : []
              ),
            ],
          });
        }

        if (
          isNewCustomer
          || isNewProduct
        ) {
          sections.push({
            kind: 'relationship',
            eyebrow:
              'Relación cliente-producto',
            title:
              'Contexto prospectivo',
            statusLabel:
              'Sin histórico conjunto',
            description:
              'No existe evidencia transaccional conjunta. '
              + 'La recomendación debe interpretarse con '
              + 'las referencias disponibles de cliente, '
              + 'producto y atributos aportados.',
            metrics: [],
          });
        } else if (
          resolvedCustomerId
          && resolvedProductId
        ) {
          sections.push({
            kind: 'relationship',
            eyebrow:
              'Relación cliente-producto',
            title:
              `${
                customer?.label
                ?? resolvedCustomerId
              } × ${
                product?.label
                ?? resolvedProductId
              }`,
            statusLabel:
              hasJointHistory
                ? 'Histórico conjunto'
                : 'Sin histórico conjunto',
            description:
              hasJointHistory
                ? (
                  'Existe actividad histórica conjunta. '
                  + 'Estas métricas describen cómo se ha '
                  + 'trabajado esta relación.'
                )
                : (
                  'Cliente y producto existen, pero no '
                  + 'se ha observado actividad conjunta.'
                ),
            metrics:
              hasJointHistory
              && relationship?.current
                ? [
                  {
                    label:
                      'Precio neto medio',
                    value:
                      formatCurrency(
                        relationship
                          .current
                          .average_net_unit_price,
                        currencyCode,
                      ),
                  },
                  {
                    label:
                      'Margen s/ingresos',
                    value:
                      formatPct(
                        relationship
                          .current
                          .margin_percentage,
                      ),
                  },
                  {
                    label:
                      'Unidades',
                    value:
                      formatNumber(
                        relationship
                          .current
                          .units,
                      ),
                  },
                  {
                    label:
                      'Ingresos',
                    value:
                      formatCurrency(
                        relationship
                          .current
                          .revenue,
                        currencyCode,
                      ),
                  },
                ]
                : [],
          });
        }

        return {
          description:
            'La recomendación se presenta junto a '
            + 'las referencias comerciales disponibles '
            + 'en el periodo seleccionado. No se infiere '
            + 'histórico donde no existe.',
          sections,
        };
      },
      [
        currencyCode,
        customer,
        customerCurrent,
        customerSchema,
        hasJointHistory,
        isNewCustomer,
        isNewProduct,
        newProductStandardCost,
        normalizedNewCustomerAttributes,
        normalizedNewProductAttributes,
        product,
        productCurrent,
        productSchema,
        relationship,
        resolvedCustomerId,
        resolvedProductId,
      ],
    );

  const loading =
    (
      Boolean(
        resolvedCustomerId,
      )
      && customerQuery.isLoading
    )
    || (
      Boolean(
        resolvedProductId,
      )
      && productQuery.isLoading
    )
    || (
      pairComplete
      && relationshipQuery.isLoading
    );

  const queryError =
    relationshipQuery
      .isError
    || customerQuery
      .isError
    || productQuery
      .isError;

  const returnLabel =
    source === 'product'
      ? 'Volver al producto'
      : 'Volver al cliente';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: {
            xs: '100vw',
            md: '94vw',
          },

          maxWidth: 1500,

          height: {
            xs: '100dvh',
            md: '90vh',
          },

          maxHeight: {
            xs: '100dvh',
            md: 980,
          },

          m: {
            xs: 0,
            md: 2,
          },

          borderRadius: {
            xs: 0,
            md: 3.5,
          },

          bgcolor: '#0b1220',
          backgroundImage:
            'none',

          border:
            '1px solid rgba(148,163,184,0.18)',

          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: {
            xs: 2,
            md: 2.25,
          },

          py: {
            xs: 1.25,
            lg: 1.05,
          },

          borderBottom:
            '1px solid rgba(148,163,184,0.14)',

          background:
            'linear-gradient(100deg, rgba(14,165,233,0.08) 0%, rgba(15,23,42,0.96) 34%, rgba(99,102,241,0.05) 100%)',

          boxShadow:
            '0 10px 30px rgba(2,6,23,0.20)',
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          spacing={1.25}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            lg: 'center',
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#38bdf8',
                  fontWeight: 850,
                  letterSpacing: 0.9,
                }}
              >
                Evaluación cliente-producto
              </Typography>

              <Chip
                size="small"
                label={
                  isNewProduct
                  || isNewCustomer
                    ? 'Contexto prospectivo'
                    : !pairComplete
                      ? 'Contexto por completar'
                      : hasJointHistory
                        ? 'Evaluación con histórico'
                        : 'Contexto prospectivo'
                }
                variant="outlined"
                sx={{
                  height: 22,
                  color: '#bae6fd',

                  borderColor:
                    'rgba(56,189,248,0.28)',
                }}
              />

              <Chip
                size="small"
                label={
                  isNewProduct
                    ? 'Producto nuevo'
                    : isNewCustomer
                      ? 'Cliente nuevo'
                      : !pairComplete
                        ? (
                          source === 'customer'
                            ? 'Selecciona producto'
                            : 'Selecciona cliente'
                        )
                        : hasJointHistory
                          ? 'Histórico conjunto disponible'
                          : 'Sin histórico conjunto'
                }
                variant="outlined"
                sx={{
                  height: 22,
                  color: '#cbd5e1',

                  borderColor:
                    'rgba(148,163,184,0.20)',
                }}
              />
            </Stack>

            <Typography
              variant="h6"
              sx={{
                mt: 0.45,
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              {
                isNewCustomer
                  ? 'Nuevo cliente'
                  : (
                    customer
                      ?.label
                    ?? resolvedCustomerId
                    ?? 'Selecciona un cliente'
                  )
              }
              {' × '}
              {
                isNewProduct
                  ? 'Nuevo producto'
                  : (
                    product
                      ?.label
                    ?? resolvedProductId
                    ?? 'Selecciona un producto'
                  )
              }
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.2,
                color: '#64748b',
              }}
            >
              Cliente {
                isNewCustomer
                  ? 'nuevo'
                  : (
                    resolvedCustomerId
                    ?? 'pendiente'
                  )
              }
              {' · '}
              Producto {
                isNewProduct
                  ? 'nuevo'
                  : (
                    resolvedProductId
                    ?? 'pendiente'
                  )
              }
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              size="small"
              variant="outlined"
              startIcon={
                <ArrowBackRoundedIcon />
              }
              onClick={
                onClose
              }
              sx={{
                textTransform:
                  'none',
                fontWeight: 800,
              }}
            >
              {returnLabel}
            </Button>

            <IconButton
              aria-label="Cerrar evaluación"
              onClick={
                onClose
              }
              sx={{
                color: '#94a3b8',

                border:
                  '1px solid rgba(148,163,184,0.12)',

                bgcolor:
                  'rgba(15,23,42,0.30)',
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {loading && (
        <LinearProgress />
      )}

      <DialogContent
        sx={{
          p: {
            xs: 2,
            md: 2.25,
          },

          overflowY: 'auto',

          bgcolor: '#08111f',
        }}
      >
        <Stack
          spacing={1.5}
        >
          {queryError && (
            <Alert
              severity="error"
            >
              No se pudo cargar el contexto comercial de
              cliente y producto.
            </Alert>
          )}

          {!pairComplete && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2.5,

                border:
                  '1px solid rgba(56,189,248,0.20)',

                background:
                  'linear-gradient(135deg, rgba(14,165,233,0.075) 0%, rgba(15,23,42,0.58) 42%, rgba(99,102,241,0.035) 100%)',

                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: {
                    xs: 1.75,
                    md: 2,
                  },

                  py: 1.4,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: '#38bdf8',
                    fontWeight: 850,
                    letterSpacing: 0.8,
                  }}
                >
                  Contexto disponible
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.2,
                    color: '#cbd5e1',
                    fontWeight: 750,
                  }}
                >
                  Completa la segunda parte de la evaluación.
                  La entidad de origen conserva todo su contexto
                  comercial mientras seleccionas la contraparte.
                </Typography>
              </Box>

              <Divider />

              <Box
                sx={{
                  display: 'grid',

                  gridTemplateColumns: {
                    xs: '1fr',
                    lg:
                      'repeat(2, minmax(0, 1fr))',
                  },
                }}
              >
                <Box
                  sx={{
                    p: {
                      xs: 1.5,
                      md: 1.75,
                    },

                    borderRight: {
                      xs: 'none',

                      lg:
                        '1px solid rgba(148,163,184,0.12)',
                    },

                    borderBottom: {
                      xs:
                        '1px solid rgba(148,163,184,0.12)',

                      lg: 'none',
                    },
                  }}
                >
                  {source === 'customer' ? (
                    customer
                    && customerCurrent ? (
                      <>
                        <Typography
                          variant="overline"
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 850,
                            letterSpacing: 0.75,
                          }}
                        >
                          Cliente
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                          }}
                        >
                          {customer.label}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                          }}
                        >
                          {customer.customer_id}
                        </Typography>

                        <Box
                          sx={{
                            mt: 1.5,

                            display: 'grid',

                            gridTemplateColumns:
                              'repeat(2, minmax(0, 1fr))',

                            gap: 1.5,
                          }}
                        >
                          <ContextMetric
                            label="Ingresos"
                            value={
                              formatCurrency(
                                customerCurrent
                                  .revenue,
                                currencyCode,
                              )
                            }
                          />

                          <ContextMetric
                            label="Margen s/ingresos"
                            value={
                              formatPct(
                                customerCurrent
                                  .margin_percentage,
                              )
                            }
                          />

                          <ContextMetric
                            label="Unidades"
                            value={
                              formatNumber(
                                customerCurrent
                                  .units,
                              )
                            }
                          />

                          <ContextMetric
                            label="Productos activos"
                            value={
                              formatNumber(
                                customerCurrent
                                  .active_products,
                              )
                            }
                          />
                        </Box>

                        <Divider
                          sx={{
                            my: 1.5,
                          }}
                        />

                        <Stack
                          spacing={0.45}
                        >
                          <AttributeLine
                            label="Segmento"
                            value={
                              customer
                                .attributes
                                .customer_segment
                            }
                          />

                          <AttributeLine
                            label="Región"
                            value={
                              customer
                                .attributes
                                .region
                            }
                          />

                          <AttributeLine
                            label="País"
                            value={
                              customer
                                .attributes
                                .country
                            }
                          />
                        </Stack>
                      </>
                    ) : (
                      <CircularProgress
                        size={22}
                      />
                    )
                  ) : (
                    <>
                      <Stack
                        direction={{
                          xs: 'column',
                          sm: 'row',
                        }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{
                          xs: 'flex-start',
                          sm: 'center',
                        }}
                      >
                        <Box>
                          <Typography
                            variant="overline"
                            sx={{
                              color: '#94a3b8',
                              fontWeight: 850,
                              letterSpacing: 0.75,
                            }}
                          >
                            Cliente a evaluar
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.3,
                              color: '#94a3b8',
                            }}
                          >
                            Selecciona un cliente existente o
                            construye un cliente nuevo para esta
                            evaluación.
                          </Typography>
                        </Box>

                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={
                            counterpartState
                          }
                          onChange={(
                            _,
                            value:
                              'existing'
                              | 'new'
                              | null,
                          ) => {
                            if (!value) {
                              return;
                            }

                            setCounterpartState(
                              value,
                            );

                            setCounterpartInput(
                              '',
                            );

                            setSelectedCounterpartId(
                              null,
                            );

                            setDebouncedSearch(
                              '',
                            );

                            setNewCustomerAttributes(
                              {},
                            );
                          }}
                        >
                          <ToggleButton
                            value="existing"
                          >
                            Existente
                          </ToggleButton>

                          <ToggleButton
                            value="new"
                          >
                            Nuevo
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>

                      {counterpartState
                        === 'existing' ? (
                        <Box
                          sx={{
                            mt: 1.25,
                          }}
                        >
                          <CounterpartSearch
                            label="Buscar cliente"
                            options={
                              counterpartOptions
                            }
                            loading={
                              counterpartSearchQuery
                                .isFetching
                            }
                            inputValue={
                              counterpartInput
                            }
                            onInputChange={(
                              value,
                            ) => {
                              setCounterpartInput(
                                value,
                              );

                              setSelectedCounterpartId(
                                null,
                              );
                            }}
                            onSelect={(
                              id,
                              label,
                            ) => {
                              setSelectedCounterpartId(
                                id,
                              );

                              setCounterpartInput(
                                label === id
                                  ? id
                                  : `${
                                    label
                                  } · ${
                                    id
                                  }`,
                              );
                            }}
                          />
                        </Box>
                      ) : (
                        <Stack
                          spacing={1.25}
                          sx={{
                            mt: 1.25,
                          }}
                        >
                          {customerSchemaQuery
                            .isLoading && (
                            <CircularProgress
                              size={22}
                            />
                          )}

                          {customerSchemaQuery
                            .isError && (
                            <Alert
                              severity="warning"
                            >
                              No se pudieron cargar los atributos
                              dinámicos del cliente.
                            </Alert>
                          )}

                          <DynamicCustomerFields
                            schema={
                              customerSchema
                            }
                            values={
                              newCustomerAttributes
                            }
                            onChange={(
                              name,
                              value,
                            ) => {
                              setNewCustomerAttributes(
                                (
                                  current,
                                ) => ({
                                  ...current,
                                  [name]:
                                    value,
                                }),
                              );
                            }}
                          />

                          {!newCustomerReady && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                lineHeight: 1.5,
                              }}
                            >
                              Completa los atributos obligatorios
                              disponibles para continuar con la
                              evaluación.
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    p: {
                      xs: 1.5,
                      md: 1.75,
                    },
                  }}
                >
                  {source === 'product' ? (
                    product
                    && productCurrent ? (
                      <>
                        <Typography
                          variant="overline"
                          sx={{
                            color: '#94a3b8',
                            fontWeight: 850,
                            letterSpacing: 0.75,
                          }}
                        >
                          Producto
                        </Typography>

                        <Typography
                          variant="h6"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                          }}
                        >
                          {product.label}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                          }}
                        >
                          {product.product_id}
                        </Typography>

                        <Box
                          sx={{
                            mt: 1.5,

                            display: 'grid',

                            gridTemplateColumns:
                              'repeat(2, minmax(0, 1fr))',

                            gap: 1.5,
                          }}
                        >
                          <ContextMetric
                            label="Ingresos"
                            value={
                              formatCurrency(
                                productCurrent
                                  .revenue,
                                currencyCode,
                              )
                            }
                          />

                          <ContextMetric
                            label="Margen s/ingresos"
                            value={
                              formatPct(
                                productCurrent
                                  .margin_percentage,
                              )
                            }
                          />

                          <ContextMetric
                            label="Precio neto medio"
                            value={
                              formatCurrency(
                                productCurrent
                                  .average_net_unit_price,
                                currencyCode,
                              )
                            }
                          />

                          <ContextMetric
                            label="Clientes activos"
                            value={
                              formatNumber(
                                productCurrent
                                  .active_customers,
                              )
                            }
                          />
                        </Box>

                        <Divider
                          sx={{
                            my: 1.5,
                          }}
                        />

                        <Stack
                          spacing={0.45}
                        >
                          <AttributeLine
                            label="Categoría"
                            value={
                              product
                                .attributes
                                .product_category
                            }
                          />

                          <AttributeLine
                            label="Marca"
                            value={
                              product
                                .attributes
                                .product_brand
                            }
                          />

                          <AttributeLine
                            label="Coste estándar"
                            value={
                              product
                                .attributes
                                .standard_cost
                                !== null
                              && product
                                .attributes
                                .standard_cost
                                !== undefined
                                ? formatCurrency(
                                  product
                                    .attributes
                                    .standard_cost,
                                  currencyCode,
                                )
                                : null
                            }
                          />
                        </Stack>
                      </>
                    ) : (
                      <CircularProgress
                        size={22}
                      />
                    )
                  ) : (
                    <>
                      <Stack
                        direction={{
                          xs: 'column',
                          sm: 'row',
                        }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{
                          xs: 'flex-start',
                          sm: 'center',
                        }}
                      >
                        <Box>
                          <Typography
                            variant="overline"
                            sx={{
                              color: '#94a3b8',
                              fontWeight: 850,
                              letterSpacing: 0.75,
                            }}
                          >
                            Producto a evaluar
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.3,
                              color: '#94a3b8',
                            }}
                          >
                            Selecciona un producto existente o
                            construye un producto nuevo para esta
                            evaluación.
                          </Typography>
                        </Box>

                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={
                            counterpartState
                          }
                          onChange={(
                            _,
                            value:
                              'existing'
                              | 'new'
                              | null,
                          ) => {
                            if (!value) {
                              return;
                            }

                            setCounterpartState(
                              value,
                            );

                            setCounterpartInput(
                              '',
                            );

                            setSelectedCounterpartId(
                              null,
                            );

                            setDebouncedSearch(
                              '',
                            );

                            setNewProductCostInput(
                              '',
                            );

                            setNewProductAttributes(
                              {},
                            );
                          }}
                        >
                          <ToggleButton
                            value="existing"
                          >
                            Existente
                          </ToggleButton>

                          <ToggleButton
                            value="new"
                          >
                            Nuevo
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>

                      {counterpartState
                        === 'existing' ? (
                        <Box
                          sx={{
                            mt: 1.25,
                          }}
                        >
                          <CounterpartSearch
                            label="Buscar producto"
                            options={
                              counterpartOptions
                            }
                            loading={
                              counterpartSearchQuery
                                .isFetching
                            }
                            inputValue={
                              counterpartInput
                            }
                            onInputChange={(
                              value,
                            ) => {
                              setCounterpartInput(
                                value,
                              );

                              setSelectedCounterpartId(
                                null,
                              );
                            }}
                            onSelect={(
                              id,
                              label,
                            ) => {
                              setSelectedCounterpartId(
                                id,
                              );

                              setCounterpartInput(
                                label === id
                                  ? id
                                  : `${
                                    label
                                  } · ${
                                    id
                                  }`,
                              );
                            }}
                          />
                        </Box>
                      ) : (
                        <Stack
                          spacing={1.25}
                          sx={{
                            mt: 1.25,
                          }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Coste estándar"
                            required
                            value={
                              newProductCostInput
                            }
                            onChange={(
                              event,
                            ) => {
                              setNewProductCostInput(
                                event
                                  .target
                                  .value,
                              );
                            }}
                            helperText={
                              'Obligatorio para evaluar un producto nuevo. '
                              + 'Debe ser mayor que cero.'
                            }
                          />

                          {productSchemaQuery
                            .isLoading && (
                            <CircularProgress
                              size={22}
                            />
                          )}

                          {productSchemaQuery
                            .isError && (
                            <Alert
                              severity="warning"
                            >
                              No se pudieron cargar los atributos
                              dinámicos del producto.
                            </Alert>
                          )}

                          <DynamicProductFields
                            schema={
                              productSchema
                            }
                            values={
                              newProductAttributes
                            }
                            onChange={(
                              name,
                              value,
                            ) => {
                              setNewProductAttributes(
                                (
                                  current,
                                ) => ({
                                  ...current,
                                  [name]:
                                    value,
                                }),
                              );
                            }}
                          />

                          {!newProductReady && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                lineHeight: 1.5,
                              }}
                            >
                              Completa el coste estándar y los
                              atributos obligatorios disponibles
                              para continuar con la evaluación.
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          {newProductReady
            && customerContext
            && customer
            && customerCurrent
            && (
              <PricingDecisionOptimizationPanel
                source="customer"
                customer={{
                  state: 'existing',
                  customer_id:
                    resolvedCustomerId,
                  attributes: {},
                }}
                product={{
                  state: 'new',
                  product_id: null,
                  attributes:
                    normalizedNewProductAttributes,
                  standard_cost:
                    newProductStandardCost,
                }}
                currencyCode={
                  currencyCode
                }
                referencePrice={
                  null
                }
                contextLabel="Producto nuevo"
                contextDetail={
                  `Cliente ${
                    resolvedCustomerId
                  } · producto nuevo · sin histórico conjunto`
                }
                contextEvidence={
                  contextEvidence
                }
                contextSupplement={
                  <PricingDecisionDynamicComparablesPanel
                    entityType="product"
                    attributes={
                      normalizedNewProductAttributes
                    }
                    contextEntityId={
                      resolvedCustomerId ?? ''
                    }
                    startDate={startDate}
                    endDate={endDate}
                  />
                }
              />
            )}

          {newCustomerReady
            && productContext
            && product
            && productCurrent
            && (
              <PricingDecisionOptimizationPanel
                source="product"
                customer={{
                  state: 'new',
                  customer_id: null,
                  attributes:
                    normalizedNewCustomerAttributes,
                }}
                product={{
                  state: 'existing',
                  product_id:
                    resolvedProductId,
                  attributes: {},
                  standard_cost:
                    null,
                }}
                currencyCode={
                  currencyCode
                }
                referencePrice={
                  null
                }
                contextLabel="Cliente nuevo"
                contextDetail={
                  `Cliente nuevo · Producto ${
                    resolvedProductId
                  } · sin histórico conjunto`
                }
                contextEvidence={
                  contextEvidence
                }
                contextSupplement={
                  <PricingDecisionDynamicComparablesPanel
                    entityType="customer"
                    attributes={
                      normalizedNewCustomerAttributes
                    }
                    contextEntityId={
                      resolvedProductId ?? ''
                    }
                    startDate={startDate}
                    endDate={endDate}
                  />
                }
              />
            )}

          {relationship
            && relationship.status
              !== 'candidate_relationship'
            && relationship.status
              !== 'ok'
            && (
              <Alert
                severity="warning"
              >
                No se pudo construir esta combinación.
                Estado: {relationship.status}.
              </Alert>
            )}

          {isEvaluablePair
            && customerContext
            && productContext
            && customer
            && product
            && customerCurrent
            && productCurrent
            && (
              <>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,

                    border:
                      '1px solid rgba(56,189,248,0.20)',

                    background:
                      'linear-gradient(135deg, rgba(14,165,233,0.075) 0%, rgba(15,23,42,0.58) 42%, rgba(99,102,241,0.035) 100%)',

                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: {
                        xs: 1.75,
                        md: 2,
                      },

                      py: 1.4,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: '#38bdf8',
                        fontWeight: 850,
                        letterSpacing: 0.8,
                      }}
                    >
                      Contexto disponible
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.2,
                        color: '#cbd5e1',
                        fontWeight: 750,
                      }}
                    >
                      {hasJointHistory
                        ? (
                          'La evaluación compara primero el '
                          + 'contexto propio del cliente y del '
                          + 'producto. El histórico conjunto '
                          + 'se conserva como evidencia adicional.'
                        )
                        : (
                          'La combinación no tiene histórico '
                          + 'conjunto. La evaluación compara la '
                          + 'evidencia propia del cliente con la '
                          + 'evidencia propia del producto.'
                        )}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      display: 'grid',

                      gridTemplateColumns: {
                        xs: '1fr',
                        lg:
                          'repeat(2, minmax(0, 1fr))',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        p: {
                          xs: 1.5,
                          md: 1.75,
                        },

                        borderRight: {
                          xs: 'none',

                          lg:
                            '1px solid rgba(148,163,184,0.12)',
                        },

                        borderBottom: {
                          xs:
                            '1px solid rgba(148,163,184,0.12)',

                          lg: 'none',
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="space-between"
                        alignItems="baseline"
                      >
                        <Box>
                          <Typography
                            variant="overline"
                            sx={{
                              color: '#94a3b8',
                              fontWeight: 850,
                              letterSpacing: 0.75,
                            }}
                          >
                            Cliente
                          </Typography>

                          <Typography
                            variant="h6"
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 900,
                            }}
                          >
                            {customer.label}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                            }}
                          >
                            {customer.customer_id}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label="Histórico disponible"
                          variant="outlined"
                          sx={{
                            color: '#a7f3d0',

                            borderColor:
                              'rgba(52,211,153,0.24)',
                          }}
                        />
                      </Stack>

                      <Box
                        sx={{
                          mt: 1.5,

                          display: 'grid',

                          gridTemplateColumns:
                            'repeat(2, minmax(0, 1fr))',

                          gap: 1.5,
                        }}
                      >
                        <ContextMetric
                          label="Ingresos"
                          value={
                            formatCurrency(
                              customerCurrent
                                .revenue,
                              currencyCode,
                            )
                          }
                          change={
                            customerChanges
                              ?.revenue_delta_pct
                              !== null
                            && customerChanges
                              ?.revenue_delta_pct
                              !== undefined
                              ? formatSignedPct(
                                customerChanges
                                  .revenue_delta_pct,
                              )
                              : undefined
                          }
                        />

                        <ContextMetric
                          label="Margen s/ingresos"
                          value={
                            formatPct(
                              customerCurrent
                                .margin_percentage,
                            )
                          }
                          change={
                            customerChanges
                              ?.margin_percentage_delta_points
                              !== null
                            && customerChanges
                              ?.margin_percentage_delta_points
                              !== undefined
                              ? formatSignedPoints(
                                customerChanges
                                  .margin_percentage_delta_points,
                              )
                              : undefined
                          }
                        />

                        <ContextMetric
                          label="Unidades"
                          value={
                            formatNumber(
                              customerCurrent
                                .units,
                            )
                          }
                          change={
                            customerChanges
                              ?.units_delta_pct
                              !== null
                            && customerChanges
                              ?.units_delta_pct
                              !== undefined
                              ? formatSignedPct(
                                customerChanges
                                  .units_delta_pct,
                              )
                              : undefined
                          }
                        />

                        <ContextMetric
                          label="Productos activos"
                          value={
                            formatNumber(
                              customerCurrent
                                .active_products,
                            )
                          }
                          change={
                            customerChanges
                              ? formatSignedInteger(
                                customerChanges
                                  .active_products_delta,
                              )
                              : undefined
                          }
                        />
                      </Box>

                      <Divider
                        sx={{
                          my: 1.5,
                        }}
                      />

                      <Stack
                        spacing={0.45}
                      >
                        <AttributeLine
                          label="Segmento"
                          value={
                            customer
                              .attributes
                              .customer_segment
                          }
                        />

                        <AttributeLine
                          label="Región"
                          value={
                            customer
                              .attributes
                              .region
                          }
                        />

                        <AttributeLine
                          label="País"
                          value={
                            customer
                              .attributes
                              .country
                          }
                        />

                        <AttributeLine
                          label="Sector"
                          value={
                            customer
                              .attributes
                              .industry_sector
                          }
                        />

                        <AttributeLine
                          label="Tamaño"
                          value={
                            customer
                              .attributes
                              .customer_size
                          }
                        />
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        p: {
                          xs: 1.5,
                          md: 1.75,
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="space-between"
                        alignItems="baseline"
                      >
                        <Box>
                          <Typography
                            variant="overline"
                            sx={{
                              color: '#94a3b8',
                              fontWeight: 850,
                              letterSpacing: 0.75,
                            }}
                          >
                            Producto
                          </Typography>

                          <Typography
                            variant="h6"
                            sx={{
                              color: '#f8fafc',
                              fontWeight: 900,
                            }}
                          >
                            {product.label}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#64748b',
                            }}
                          >
                            {product.product_id}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label="Histórico disponible"
                          variant="outlined"
                          sx={{
                            color: '#a7f3d0',

                            borderColor:
                              'rgba(52,211,153,0.24)',
                          }}
                        />
                      </Stack>

                      <Box
                        sx={{
                          mt: 1.5,

                          display: 'grid',

                          gridTemplateColumns:
                            'repeat(2, minmax(0, 1fr))',

                          gap: 1.5,
                        }}
                      >
                        <ContextMetric
                          label="Ingresos"
                          value={
                            formatCurrency(
                              productCurrent
                                .revenue,
                              currencyCode,
                            )
                          }
                          change={
                            productChanges
                              ?.revenue_delta_pct
                              !== null
                            && productChanges
                              ?.revenue_delta_pct
                              !== undefined
                              ? formatSignedPct(
                                productChanges
                                  .revenue_delta_pct,
                              )
                              : undefined
                          }
                        />

                        <ContextMetric
                          label="Margen s/ingresos"
                          value={
                            formatPct(
                              productCurrent
                                .margin_percentage,
                            )
                          }
                          change={
                            productChanges
                              ?.margin_percentage_delta_points
                              !== null
                            && productChanges
                              ?.margin_percentage_delta_points
                              !== undefined
                              ? formatSignedPoints(
                                productChanges
                                  .margin_percentage_delta_points,
                              )
                              : undefined
                          }
                        />

                        <ContextMetric
                          label="Precio neto medio"
                          value={
                            formatCurrency(
                              productCurrent
                                .average_net_unit_price,
                              currencyCode,
                            )
                          }
                        />

                        <ContextMetric
                          label="Clientes activos"
                          value={
                            formatNumber(
                              productCurrent
                                .active_customers,
                            )
                          }
                          change={
                            productChanges
                              ? formatSignedInteger(
                                productChanges
                                  .active_customers_delta,
                              )
                              : undefined
                          }
                        />
                      </Box>

                      <Divider
                        sx={{
                          my: 1.5,
                        }}
                      />

                      <Stack
                        spacing={0.45}
                      >
                        <AttributeLine
                          label="Categoría"
                          value={
                            product
                              .attributes
                              .product_category
                          }
                        />

                        <AttributeLine
                          label="Marca"
                          value={
                            product
                              .attributes
                              .product_brand
                          }
                        />

                        <AttributeLine
                          label="Coste estándar"
                          value={
                            product
                              .attributes
                              .standard_cost
                              !== null
                            && product
                              .attributes
                              .standard_cost
                              !== undefined
                              ? formatCurrency(
                                product
                                  .attributes
                                  .standard_cost,
                                currencyCode,
                              )
                              : null
                          }
                        />

                        <AttributeLine
                          label="Unidad"
                          value={
                            product
                              .attributes
                              .unit_of_measure
                          }
                        />
                      </Stack>
                    </Box>
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      px: {
                        xs: 1.75,
                        md: 2,
                      },

                      py: 1.15,

                      bgcolor:
                        'rgba(15,23,42,0.26)',
                    }}
                  >
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      spacing={0.75}
                      justifyContent="space-between"
                      alignItems={{
                        xs: 'flex-start',
                        sm: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 850,
                        }}
                      >
                        {hasJointHistory
                          ? 'Histórico conjunto disponible'
                          : 'Sin histórico conjunto'}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#94a3b8',
                        }}
                      >
                        {hasJointHistory
                          ? (
                            relationship
                              ?.activity
                              ?.last_purchase_date_current
                              ? (
                                'Existe actividad conjunta en '
                                + 'el periodo analizado.'
                              )
                              : (
                                `La combinación tiene histórico, `
                                + `pero no actividad en el periodo `
                                + `actual. Última compra: ${
                                  relationship
                                    ?.activity
                                    ?.last_purchase_date
                                  ?? '—'
                                }.`
                              )
                          )
                          : (
                            'No se presentan métricas de relación '
                            + 'porque esta combinación no se ha '
                            + 'observado transaccionalmente.'
                          )}
                      </Typography>
                    </Stack>
                  </Box>
                </Paper>

                <PricingDecisionOptimizationPanel
                  source={source}
                  customer={{
                    state: 'existing',
                    customer_id:
                      resolvedCustomerId,
                    attributes: {},
                  }}
                  product={{
                    state: 'existing',
                    product_id:
                      resolvedProductId,
                    attributes: {},
                    standard_cost:
                      null,
                  }}
                  currencyCode={
                    currencyCode
                  }
                  referencePrice={
                    hasJointHistory
                      ? relationship
                          ?.current
                          ?.average_net_unit_price
                        ?? null
                      : null
                  }
                  contextLabel={
                    hasJointHistory
                      ? 'Evaluación con histórico conjunto'
                      : 'Contexto prospectivo fijado'
                  }
                  contextDetail={
                    `Cliente ${
                      resolvedCustomerId
                    } · Producto ${
                      resolvedProductId
                    } · ${
                      hasJointHistory
                        ? 'histórico conjunto disponible'
                        : 'sin histórico conjunto'
                    }`
                  }
                  contextEvidence={
                    contextEvidence
                  }
                  contextSupplement={
                    <CustomerRelationshipBenchmarkPanel
                      customerId={
                        resolvedCustomerId
                        ?? ''
                      }
                      productId={
                        resolvedProductId
                        ?? ''
                      }
                      startDate={
                        startDate
                      }
                      endDate={
                        endDate
                      }
                    />
                  }
                />
              </>
            )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}